/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentChildren, ComponentProps, Ref, JSX } from 'preact';
import { forwardRef, useImperativeHandle, useRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

import { FocusableHandle } from '#hooks/UNSAFE_useFocusableTextField';
import { useCollectionFocusRing } from '#hooks/PRIVATE_useCollectionFocusRing';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { Size } from '#utils/UNSAFE_size';
import { BundleType } from '#resources/nls/bundle';
import { Label } from '#UNSAFE_Label';
import {
  InlineHelp,
  InlineUserAssistance,
  InlineUserAssistanceContainer,
  UserAssistanceDensityType
} from '#UNSAFE_UserAssistance';
import { useCheckboxRadioField } from './useCheckboxRadioField';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { topUAContainerStyles } from './CheckboxRadioFieldStyles.css';
import { CheckboxRadioContext } from './CheckboxRadioContext';
import { layoutSpanStyles, LayoutColumnSpan } from '#utils/UNSAFE_styles/Layout';
import { type TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { CheckboxRadioLabelValueLayout } from '#UNSAFE_CheckboxRadioField/CheckboxRadioLabelValueLayout';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type AriaProps = Pick<UnpackSignals<JSX.AriaAttributes>, 'aria-describedby'>;

type Props = TestIdProps &
  AriaProps & {
    /**
     * Text to provide guidance to help the user understand what data to enter.
     */
    assistiveText?: InlineUserAssistanceProps['assistiveText'];
    /**
     * baseId, used to generate other element IDs.
     */
    baseId: string;
    /**
     * <RadioItem /> or <CheckboxItem />
     */
    children: ComponentChildren;
    /**
     * Specifies how many columns to span in a FormLayout with direction === 'row'
     */
    columnSpan?: LayoutColumnSpan;
    /**
     * Value of the radio option.
     */
    hasValue?: boolean;
    /**
     * Help source URL associated with the component.
     */
    helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
    /**
     * Custom text to be rendered for the <code>helpSourceLink</code>.
     */
    helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
    /**
     * Specifies if the radio group is disabled.
     */
    isDisabled?: boolean;
    /**
     * Specifies if the radio group is read-only.
     */
    isReadonly?: boolean;
    /**
     * Specifies whether the radio group requires a selection to be made.
     */
    isRequired?: boolean;
    /**
     * Label text for the group of radio buttons.
     */
    label: string;
    /**
     * Specifies where the label is positioned relative to the field.
     */
    labelEdge?: 'none' | 'top' | 'start' | 'inside';
    /**
     * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
     */
    labelStartWidth?: Size;
    /**
     * Messages to show on screen that are associated with the component.
     */
    messages?: InlineUserAssistanceProps['messages'];

    role?: 'radiogroup' | 'group';
    /**
     * Specifies the density of the user assistance presentation.  It can be set to:
     * <ul>
     * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
     * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * <li><code>'compact'</code>: Show inline and reserve space to prevent layout reflow when user
     * assistance text is displayed.</li>
     * </ul>
     */
    userAssistanceDensity?: UserAssistanceDensityType;

    parentComponentVariant: 'radioSet' | 'checkboxSet';
  };

export const CheckboxRadioField = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      baseId,
      columnSpan = 1,
      hasValue,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isReadonly,
      isRequired,
      label,
      labelEdge,
      labelStartWidth = '33%',
      parentComponentVariant,
      messages,
      userAssistanceDensity,
      role,
      children,
      testId
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const testIdProps = useTestId(testId);
    const UAtestId = `${testId}-inline-user-assistance`;
    const UAtestIdTop = testId ? `${UAtestId}-top` : undefined;
    const UAtestIdBottom = testId ? `${UAtestId}-bottom` : undefined;
    const hasTopUserAssistance =
      !isDisabled && !isReadonly && (!!assistiveText || !!helpSourceLink);
    // we still render <InlineUserAssistance/> if density is 'efficient' or 'compact' to save space,
    // even though we don't render user assistance for disabled fields
    const hasBottomUserAssistance = !isDisabled;
    const groupRef = useRef<HTMLDivElement>(null);

    const {
      groupProps,
      labelProps,
      topUserAssistanceProps,
      bottomUserAssistanceProps,
      readonlyId
    } = useCheckboxRadioField({
      ariaDescribedBy,
      baseId,
      isRequired,
      isReadonly,
      isDisabled,
      label,
      labelEdge,
      messages,
      hasValue,
      hasTopUserAssistance,
      hasBottomUserAssistance,
      parentComponentVariant
    });

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const readonlyLabel = translations.formControl_readOnly();
    const isRadioSet = parentComponentVariant === 'radioSet';

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (!isDisabled) {
          const inputChildren = groupRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [];
          const selectedChild = Array.from(inputChildren).find((child) => child.checked);

          if (selectedChild) {
            selectedChild.focus();
          } else {
            inputChildren[0].focus();
          }
        }
      },
      blur: () => {
        if (groupRef.current?.contains(document.activeElement)) {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }));

    // the props from the hook are used for focus management on the radioset variant to allow for arrow navigation
    // while checkboxset uses tabbing
    const [isFocusRingShown, focusRingProps] = useCollectionFocusRing(
      groupRef,
      isRadioSet ? ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'] : ['Tab']
    );

    const content = (
      <>
        {hasTopUserAssistance && (
          <div
            id={`${baseId}-top-ua-container`}
            class={labelEdge === 'start' ? topUAContainerStyles.start : topUAContainerStyles.top}>
            <InlineUserAssistanceContainer {...topUserAssistanceProps} testId={UAtestIdTop}>
              <InlineHelp
                assistiveText={assistiveText}
                sourceLink={helpSourceLink}
                sourceText={helpSourceText}
              />
            </InlineUserAssistanceContainer>
          </div>
        )}
        <CheckboxRadioContext.Provider value={{ isFocusRingShown }}>
          {children}
        </CheckboxRadioContext.Provider>
        {hasBottomUserAssistance ? (
          <InlineUserAssistance
            {...bottomUserAssistanceProps}
            requiredAlignment="start"
            messages={messages}
            userAssistanceDensity={userAssistanceDensity === 'reflow' ? 'reflow' : 'efficient'}
            testId={UAtestIdBottom}
          />
        ) : userAssistanceDensity === 'reflow' ? undefined : (
          /* save space for user assistance if density is 'efficient' or 'compact', even though we don't
             render user assistance for disabled fields */
          <InlineUserAssistance
            id={bottomUserAssistanceProps?.id}
            userAssistanceDensity="efficient"
            testId={UAtestIdBottom}
          />
        )}
      </>
    );
    // The top level element needs the column span class on it.
    const classes = layoutSpanStyles.layoutSpanColumn[columnSpan];

    return (
      <div
        role={role}
        ref={groupRef}
        class={classes}
        {...groupProps}
        {...focusRingProps}
        {...testIdProps}>
        {labelEdge === 'none' ? (
          content
        ) : (
          <CheckboxRadioLabelValueLayout
            columnSpan={columnSpan}
            parentComponentVariant={parentComponentVariant}
            hasTopUserAssistance={hasTopUserAssistance}
            label={
              <Label
                {...labelProps}
                variant={labelEdge}
                userAssistanceDensity="efficient"
                parentComponentVariant={parentComponentVariant}>
                {label}
              </Label>
            }
            labelEdge={labelEdge}
            labelStartWidth={labelStartWidth}>
            {content}
          </CheckboxRadioLabelValueLayout>
        )}
        {isReadonly && (
          <HiddenAccessible id={readonlyId} isHidden={true}>
            {readonlyLabel}
          </HiddenAccessible>
        )}
      </div>
    );
  }
);
